import React, {FC, useEffect, useState} from "react";
import {App2AuthContext, type App2AuthContextValue} from "@/app2/auth";
import {IUser} from "@buildwitt/shapeshift-types";
import {Capacitor} from "@capacitor/core";
import {Browser} from "@capacitor/browser";
import {App} from "@capacitor/app";
import {Preferences} from "@capacitor/preferences";
import Hotjar from "@hotjar/browser";
import {attemptTokenRefresh} from "@/utils/attemptTokenRefresh";

// OAuth configuration
const clientId = process.env.REACT_APP_PROPELAUTH_CLIENT_ID;
const androidRedirectUri = process.env.REACT_APP_PROPELAUTH_ANROID_REDIRECT_URI;
const authUrl = process.env.REACT_APP_PROPELAUTH_URL;
const secret = process.env.REACT_APP_PROPELAUTH_SECRET;
const authorizationEndpoint = authUrl + "/propelauth/oauth/authorize";

if (!secret)
	throw new Error("Failed to get REACT_APP_PROPELAUTH_SECRET env")

if (!authUrl)
	throw new Error("Failed to get REACT_APP_PROPELAUTH_URL env")

if (!androidRedirectUri)
	throw new Error("Failed to get REACT_APP_PROPELAUTH_ANROID_REDIRECT_URI env")

if (!clientId)
	throw new Error("Failed to get REACT_APP_PROPELAUTH_CLIENT_ID env")

if (!process.env.REACT_APP_BASE_URL)
	throw new Error("Failed to get REACT_APP_BASE_URL env")

if (!process.env.REACT_APP_API_HOST)
	throw new Error("Failed to get REACT_APP_API_HOST env")

interface IProps {
	children: JSX.Element
}

const generateState = (length: number): string => {
	const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	let state = "";
	for (let i = 0; i < length; i++) {
		state += charset.charAt(Math.floor(Math.random() * charset.length));
	}
	return state;
}

export const getSafePlatform = (): "ios" | "android" | "web" => {
	const platform = Capacitor.getPlatform();
	let result: "ios" | "android" | "web";

	if (platform === "ios" || platform === "android") {
		result = platform;
	} else {
		const ua = navigator.userAgent;

		if (/iPad|iPhone|iPod/.test(ua)) {
			result = "ios";
		} else if (/Android/.test(ua)) {
			result = "android";
		} else {
			result = "web";
		}
	}
	return result;
};

const checkShouldRedirectToIosApp = (platform: string): boolean => (window?.location?.href?.includes("?code") &&
	new URL(window.location.href).searchParams.get("state")?.split("_")?.[1] === "ios" &&
	platform === "web")

const AuthProviderInner: FC<IProps> = ({children}) => {
	const [accessToken, setAccessToken] = useState<string>();
	const [refreshToken, setRefreshToken] = useState<string>();
	const [expiresIn, setExpiresIn] = useState<number>();
	const [user, setUser] = useState<IUser & { profileImage: string, organizationId?: string, organizationName?: string, externalId?: string }>();
	const [authenticated, setAuthenticated] = useState(false);
	const [authReady, setAuthReady] = useState(false);
	const [platform] = useState(Capacitor.getPlatform());
	const state = generateState(16) + "_" + getSafePlatform();
	const redirect_uri = platform === "web" ? process.env.REACT_APP_BASE_URL : androidRedirectUri;
	if (!redirect_uri)
		throw new Error("Failed to get redirect_uri for exchangeCodeForToken in AuthProvider")

	if (!process.env.REACT_APP_API_HOST)
		throw new Error("Please set REACT_APP_API_HOST in .env");
	const [contextConfig] = useState({baseApiUrl: process.env.REACT_APP_API_HOST});

	const clearStored = async (): Promise<void> => {
		if (platform === "android" || platform === "ios") {
			await Preferences.remove({key: "access"});
			await Preferences.remove({key: "refresh"});
			await Preferences.remove({key: "expiresIn"});
			await Preferences.remove({key: "user"});
		}
		else if (platform === "web") {
			localStorage.clear();
		}
	}

	const getUserInfo = async (token: string): Promise<void> => {
		const userEndpoint = authUrl + "/propelauth/oauth/userinfo";

		const response = await fetch(userEndpoint, {
			method: "GET",
			headers: {
				"Authorization": `Bearer ${token}`
			}
		});

		const data = await response.json();
		const orgId = Object.keys(data?.org_id_to_org_info ?? {})?.[0]
		const orgInfo = data?.org_id_to_org_info?.[orgId];
		const orgName = orgInfo?.org_name ?? "";

		setUser({
			firstname: data.first_name, lastname: data.last_name,
			blocked: false, confirmed: true, username: data.email,
			email: data.email, createdAt: new Date(data.created_at).toLocaleString(),
			updatedAt: "", provider: "", profileImage: data.picture_url,
			organizationId: orgId, organizationName: orgName, externalId: data.user_id
		} as (IUser & { profileImage: string, organizationId?: string, organizationName?: string, externalId?: string }))
	}

	const exchangeCodeForToken = async (code: string): Promise<void> => {
		const tokenEndpoint = authUrl + "/propelauth/oauth/token";
		const response = await fetch(tokenEndpoint, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
				"Authorization": `Basic ${secret}`
			},
			body: new URLSearchParams({
				state,
				client_id: clientId,
				grant_type: "authorization_code",
				code: code,
				redirect_uri: redirect_uri
			})
		});

		const data = await response.json();

		const expiryTime = (Date.now() + data.expires_in * 1000);
		setAccessToken(data.access_token)
		setRefreshToken(data.refresh_token)
		setExpiresIn(expiryTime)
		await getUserInfo(data.access_token);

		if (window.location.href.includes("?code")) {
			const url = new URL(window.location.href);
			url.searchParams.delete("code");
			url.searchParams.delete("state");
			window.history.replaceState({}, "", url.toString());
		}

		setAuthenticated(true);
		if (Capacitor.isNativePlatform()) {
			try { await Browser.close(); } catch (_) { void 0 }
		}
	}

	const refreshAccessToken = async (): Promise<void> => {
		const tokenEndpoint = authUrl + "/propelauth/oauth/token";

		if (!refreshToken)
			return;

		const response = await fetch(tokenEndpoint, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded"
			},
			body: new URLSearchParams({
				client_id: clientId,
				grant_type: "refresh_token",
				refresh_token: refreshToken
			})
		});

		if (!response.ok) {
			void handleLogout();
			return;
		}

		const data = await response.json();
		const {access_token: newAccessToken, refresh_token: newRefreshToken, expires_in: newExpiresIn} = data;
		const expiryTime = (Date.now() + newExpiresIn * 1000); // Convert seconds to milliseconds

		setAccessToken(newAccessToken)
		setRefreshToken(newRefreshToken)
		setExpiresIn(expiryTime)
	}

	const loginWithOAuth = async (): Promise<void> => {
		if (window?.location?.href?.includes("?code")) {

			const url = new URL(window.location.href);
			const authorizationCode = url.searchParams.get("code");
			const authorizationState = url.searchParams.get("state");

			if (checkShouldRedirectToIosApp(platform)) {
				const customScheme = `bwtrain://callback?code=${authorizationCode}&state=${authorizationState}`;
				window.location.replace(customScheme);
				if (authorizationCode)
					await exchangeCodeForToken(authorizationCode);
			}

			else if (authorizationCode) {
				await exchangeCodeForToken(authorizationCode);
			}
			return;
		}

		const _redirect_uri = encodeURIComponent(redirect_uri);
		const url = `${authorizationEndpoint}?client_id=${clientId}&response_type=code&redirect_uri=${_redirect_uri}&scope=openid%20profile%20email&state=${state}`;

		if (platform === "android" || platform === "ios") {
			await Browser.open({url});
			App.addListener("appUrlOpen", async (data) => {
				const u = new URL(data.url);
				const authorizationCode = u.searchParams.get("code");

				if (authorizationCode) {
					await exchangeCodeForToken(authorizationCode);
				}
				await Browser.close();
			});
		}
		else {
			window.location.href = url
		}
	}

	const handleLogout = async (): Promise<void> => {
		const logoutEndpoint = authUrl + "/api/backend/v1/logout";
		if (refreshToken)
			await fetch(logoutEndpoint, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({
					"refresh_token": refreshToken
				})
			});
		await clearStored();
		setAuthenticated(false);
		setUser(undefined);
		setAccessToken(undefined);
		setRefreshToken(undefined);
		setExpiresIn(undefined);
		if (window.location.pathname !== "/" && window.location.pathname !== "/callback") {
			window.location.replace("/");
		}
	};

	useEffect(() => {
		const siteId = process.env.REACT_APP_HOTJAR_ID;
		const hotjarVersion = 6;

		if (siteId)
			Hotjar.init(parseInt(siteId), hotjarVersion);
	}, [])

	useEffect(() => {
		const handleInitialLaunch = async () => {
			try {
				const launchUrl = await App.getLaunchUrl();

				if (launchUrl?.url) {
					const url = new URL(launchUrl.url);
					const authorizationCode = url.searchParams.get("code");

					if (authorizationCode) {
						await exchangeCodeForToken(authorizationCode);
						if (Capacitor.isNativePlatform()) {
							try { await Browser.close(); } catch (_) { void 0 }
						}
					}
				}
			} catch (err) {
				console.error("Error checking launch URL:", err);
			}
		};

		handleInitialLaunch();
	}, []);


	useEffect(() => {
		const listener = App.addListener("appUrlOpen", async (data) => {
			if (data.url.includes("?code")) {
				const url = new URL(data.url);
				const authorizationCode = url.searchParams.get("code");
				if (authorizationCode) {
					await exchangeCodeForToken(authorizationCode);
					if (Capacitor.isNativePlatform()) {
						try { await Browser.close(); } catch (_) { void 0 }
					}
				}
			}
		});

		return () => {
			listener.remove();
		};
	}, []);

	useEffect(() => {
		const fnc = async (): Promise<void> => {
			try {
				const isRxdRoute = window.location.pathname.includes("/rxd/");
				if (isRxdRoute) {
					return;
				}

				const isLandingPage = window.location.pathname === "/" || window.location.pathname === "/callback";

				const hasOAuthCode = window?.location?.href?.includes("?code");
				if (hasOAuthCode) {
					await loginWithOAuth();
					return;
				}

				const token: { refresh?: string | null, access?: string | null, expiresIn?: string | null, user?: string | null } = {};
				if (platform === "android" || platform === "ios") {
					try {
						token.access = (await Preferences.get({key: "access"})).value;
						token.refresh = (await Preferences.get({key: "refresh"})).value;
						token.expiresIn = (await Preferences.get({key: "expiresIn"})).value;
						token.user = (await Preferences.get({key: "user"})).value;
					} catch (error) {
						// eslint-disable-next-line no-console
						console.error("Error retrieving SecureStoragePlugin token", error);
					}
				}
				else if (platform === "web") {
					try {
						token.access = localStorage.getItem("access");
						token.refresh = localStorage.getItem("refresh");
						token.expiresIn = localStorage.getItem("expiresIn");
						token.user = localStorage.getItem("user");
					} catch (error) {
						// eslint-disable-next-line no-console
						console.error("Error retrieving localStorage token", error);
					}
				}

				if (token.expiresIn && (parseInt(token.expiresIn)) < new Date().getTime()) {
					if (token.refresh) {
						try {
							const tokenEndpoint = authUrl + "/propelauth/oauth/token";
							const result = await attemptTokenRefresh(tokenEndpoint, clientId, token.refresh);

							if (result.success && result.access_token && result.refresh_token && result.expires_in) {
								const expiryTime = Date.now() + result.expires_in * 1000;
								setAccessToken(result.access_token);
								setRefreshToken(result.refresh_token);
								setExpiresIn(expiryTime);

								if (token.user) {
									setUser(JSON.parse(token.user));
								} else {
									await getUserInfo(result.access_token);
								}

								setAuthenticated(true);
							} else {
								await clearStored();
								if (!isLandingPage) {
									await loginWithOAuth();
								}
							}
						} catch {
							await clearStored();
							if (!isLandingPage) {
								await loginWithOAuth();
							}
						}
					} else if (!isLandingPage) {
						await loginWithOAuth();
					}
				}

				else if (token.access && token.refresh && token.expiresIn) {
					setAccessToken(token.access);
					setRefreshToken(token.refresh)
					setExpiresIn(parseInt(token.expiresIn))

					if (token.user) {
						setUser(JSON.parse(token.user))
					} else {
						await getUserInfo(token.access)
					}

					setAuthenticated(true)
				}

				else {
					if (!isLandingPage) {
						await loginWithOAuth();
						await clearStored()
					}
				}
			} finally {
				setAuthReady(true);
			}
		}

		if (authenticated)
			return;

		void fnc();
	}, [authenticated])

	useEffect(() => {
		if (!accessToken || !expiresIn)
			return;

		const checkTokenInterval = setInterval(async () => {
			try {
				await refreshAccessToken();
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error("Error refreshing access token:", error);
				await loginWithOAuth();
			}
		}, expiresIn - Date.now() - 360000);

		return () => clearInterval(checkTokenInterval);
	}, [accessToken, expiresIn]);

	useEffect(() => {
		const storeTokens = async (at?: string, rt?: string, ex?: number): Promise<void> => {
			if (!(rt && at && ex))
				return;

			if (platform === "android" || platform === "ios") {
				await Preferences.set({key: "access", value: at});
				await Preferences.set({key: "refresh", value: rt});
				await Preferences.set({key: "expiresIn", value: ex.toString()});
			}
			else if (platform === "web") {
				localStorage.setItem("access", at);
				localStorage.setItem("refresh", rt);
				localStorage.setItem("expiresIn", ex.toString());
			}
		}

		storeTokens(accessToken, refreshToken, expiresIn)
	}, [accessToken, refreshToken, expiresIn, platform])

	useEffect(() => {
		const storeUser = async (u?: IUser): Promise<void> => {
			if (!(u))
				return;

			if (platform === "android" || platform === "ios") {
				await Preferences.set({key: "user", value: JSON.stringify(u)});
			}
			else if (platform === "web") {
				localStorage.setItem("user", JSON.stringify(u));
			}
		}

		storeUser(user)
	}, [user, platform])

	const authContextValue: App2AuthContextValue = {
		authReady,
		jwt: accessToken,
		setJwt: (token: string) => {
			setAccessToken(token);
		},
		user,
		setUser,
		authenticated: !!(authenticated && accessToken && user),
		config: contextConfig,
		logout: () => {
			void handleLogout();
		},
		loginWithOAuth,
		onUauthorizedResponse: () => {
			void refreshAccessToken();
		},
		onNetworkError: () => {/* ignore */}
	};

	const isRxdRoute = window.location.pathname.includes("/rxd/");
	if (isRxdRoute) {
		return <>{children}</>;
	}

	return <App2AuthContext.Provider value={authContextValue}>{children}</App2AuthContext.Provider>;
}


const AuthProvider = React.memo(AuthProviderInner);

export default AuthProvider;
